import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    CircularProgress,
    IconButton,
    Snackbar,
    Grid,
    Alert, // Import Alert for better snackbar presentation
} from '@mui/material';
import { gql, useMutation } from '@apollo/client';
import CloseIcon from '@mui/icons-material/Close';

const EDIT_IMEI_OR_ENGINE_NO = gql`
    mutation Mutation($vehicleId: String!, $newImei: String, $newEngineNo: String) {
        editImeiOrEngineNo(vehicleId: $vehicleId, newImei: $newImei, newEngineNo: $newEngineNo) {
            status
            message
        }
    }
`;

interface EditDetailsDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: (updatedValues: { vehicleId: string; imei: string; engineNo: string }) => void;
    data: {
        vehicleId: string;
        vin: string;
        engineNo: string;
        imei: string;
    };
}

const EditDetailsDialog: React.FC<EditDetailsDialogProps> = ({ open, onClose, onSuccess, data }) => {
    const [form, setForm] = useState({
        imei: data.imei || '',
        engineNo: data.engineNo || '',
    });
    const [originalValues, setOriginalValues] = useState({
        imei: data.imei || '',
        engineNo: data.engineNo || '',
    });
    const [alert, setAlert] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info';
    } | null>(null);
    const [imeiError, setImeiError] = useState<string | null>(null);
    const [engineNoError, setEngineNoError] = useState<string | null>(null);

    useEffect(() => {
        // Reset form and errors when dialog opens or data changes
        setForm({
            imei: data.imei || '',
            engineNo: data.engineNo || '',
        });
        setOriginalValues({
            imei: data.imei || '',
            engineNo: data.engineNo || '',
        });
        setImeiError(null);
        setEngineNoError(null);
        setAlert(null);
    }, [data, open]);

    const [editDetails, { loading }] = useMutation(EDIT_IMEI_OR_ENGINE_NO, {
        onCompleted: (response) => {
            if (response?.editImeiOrEngineNo?.status) {
                const hasChanges = form.imei !== originalValues.imei || form.engineNo !== originalValues.engineNo;

                if (hasChanges) {
                    setAlert({
                        open: true,
                        message: response.editImeiOrEngineNo.message || 'Details updated successfully',
                        severity: 'success'
                    });
                    setTimeout(() => {
                        onSuccess({
                            vehicleId: data.vehicleId,
                            imei: form.imei,
                            engineNo: form.engineNo
                        });
                        onClose();
                    }, 2000);
                } else {
                    setAlert({ open: true, message: 'No changes were made', severity: 'info' });
                    setTimeout(() => {
                        onClose();
                    }, 2000);
                }
            } else {
                setAlert({
                    open: true,
                    message: response.editImeiOrEngineNo.message || 'Failed to update details',
                    severity: 'error'
                });
            }
        },
        onError: (error) => {
            console.error("Error editing details:", error);
            setAlert({ open: true, message: error.message || 'An unexpected error occurred', severity: 'error' });
        },
    });

    const handleChange = (field: 'imei' | 'engineNo', value: string) => {
        let processedValue = value;

        if (field === 'imei') {
            processedValue = value.replace(/[^0-9]/g, '').slice(0, 15);
            if (processedValue.length > 0 && (processedValue.length < 10 || processedValue.length > 15)) {
                setImeiError('IMEI must be between 10-15 digits');
            } else {
                setImeiError(null);
            }
        } else if (field === 'engineNo') {
            processedValue = value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 20);
            if (processedValue.length > 0 && processedValue.length < 5) {
                setEngineNoError('Engine number must be at least 5 characters');
            } else {
                setEngineNoError(null);
            }
        }

        setForm(prev => ({ ...prev, [field]: processedValue }));
    };

    const handleSubmit = async () => {
        // Re-validate just before submission to catch any last-minute issues
        let isValid = true;
        if (form.imei.length > 0 && (form.imei.length < 10 || form.imei.length > 15)) {
            setImeiError('IMEI must be between 10-15 digits');
            isValid = false;
        } else {
            setImeiError(null);
        }

        if (form.engineNo.length > 0 && form.engineNo.length < 5) {
            setEngineNoError('Engine number must be at least 5 characters');
            isValid = false;
        } else {
            setEngineNoError(null);
        }

        if (!isValid) {
            setAlert({ open: true, message: 'Please fix the errors before submitting', severity: 'error' });
            return;
        }

        // Check if no changes were made and prevent API call if so
        if (form.imei === originalValues.imei && form.engineNo === originalValues.engineNo) {
            setAlert({ open: true, message: 'No changes to submit', severity: 'info' });
            // Close the dialog after a short delay to show the "no changes" message
            setTimeout(() => {
                onClose();
            }, 50);
            return;
        }

        try {
            await editDetails({
                variables: {
                    vehicleId: data.vehicleId,
                    newImei: form.imei !== originalValues.imei ? form.imei : null,
                    newEngineNo: form.engineNo !== originalValues.engineNo ? form.engineNo : null,
                },
            });
            // onCompleted will handle success and close dialog
        } catch (error) {
            // Error handling is already done in onError callback
        }
    };

    const handleCloseAlert = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlert(null);
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        width: '100%',
                        maxWidth: { xs: '100vw', sm: 500, md: 500 },
                        m: { xs: 1, sm: 2 },
                    },
                }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Edit Device Details</Typography>
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        size="small"
                        sx={{ ml: 1, color: 'inherit' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: { xs: 1, sm: 3 } }}>
                    <Grid container spacing={2}>
                        <Grid sx={{ xs:12}} > 
                            <TextField
                                label="VIN"
                                value={data.vin || "N/A"}
                                fullWidth
                                margin="dense"
                                disabled
                            />
                        </Grid>
                        <Grid  sx={{ xs:12}} > 
                            <TextField
                                label="Engine Number"
                                value={form.engineNo}
                                onChange={(e) => handleChange('engineNo', e.target.value)}
                                fullWidth
                                margin="dense"
                                required
                                error={!!engineNoError}
                                helperText={engineNoError}
                                inputProps={{
                                    maxLength: 32, 
                                    onInput: (e: any) => {
                                        e.target.value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                                    }
                                }}
                            />
                        </Grid>
                        <Grid  sx={{ xs:12}} > 
                            <TextField
                                label="IMEI Number"
                                value={form.imei}
                                onChange={(e) => handleChange('imei', e.target.value)}
                                fullWidth
                                margin="dense"
                                required
                                error={!!imeiError}
                                helperText={imeiError}
                                inputProps={{
                                    maxLength: 15,
                                    minLength: 10,
                                    onInput: (e: any) => {
                                        e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ p: 3 }}>
                    <Button
                        onClick={onClose}
                        disabled={loading}
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={
                            loading ||
                            !!imeiError || 
                            !!engineNoError || 
                            !form.imei.trim() ||
                            !form.engineNo.trim() || 
                            (form.imei === originalValues.imei && form.engineNo === originalValues.engineNo) 
                        }
                        color='error'
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!alert}
                autoHideDuration={6000}
                onClose={handleCloseAlert}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert
                    onClose={handleCloseAlert}
                    severity={alert?.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {alert?.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default EditDetailsDialog;